import type { BoardDoc } from '../doc/BoardDoc';
import { DEG, type Point } from '../doc/geometry';
import type { NewItem, StrokeItem } from '../doc/schema';
import { safeKeyBetween } from '../doc/zorder';
import { fillTouches, isFill, isInk, strokeHalfWidth, strokeHit, strokeScale, strokeWorldPoints, worldToBox } from './ink';
import { paintStroke } from './items/StrokeItem';
import type { InkPreview } from './paneStore';

/**
 * The eraser. In whole mode it removes every stroke it touches, as it goes. Otherwise it erases
 * only what it covers: a line is cut where the eraser crosses it and the pieces on either side
 * become strokes of their own (the first keeps the id), while a fill keeps its shape and records
 * the eraser's path as a mask, which the renderers subtract.
 *
 * Partial erasing works on a copy while the pointer is down, shown through the pane's ink
 * preview, and writes the document once when it lifts: rubbing back and forth over a long
 * stroke would otherwise rewrite (and sync, and keep for undo) its whole point list every frame.
 */
export interface EraseGesture {
  whole: boolean;
  last: Point;
  lines: Map<string, LineWork>;
  fills: Map<string, FillWork>;
  /** What the pane draws instead of the strokes touched so far. */
  preview: Record<string, InkPreview>;
}

/** A line being cut: its pieces so far, in the item's box frame. */
interface LineWork {
  item: StrokeItem;
  runs: Run[];
}

/** A fill being rubbed: its mask paths so far, and which one this drag is extending. */
interface FillWork {
  item: StrokeItem;
  erased: number[][];
  open: number | null;
}

/** A stretch of a line: flat [u, v, pressure, ...] in the item's box frame (world units). */
interface Run {
  p: number[];
  /** Starts at the stroke's first point / ends at its last one. */
  head: boolean;
  tail: boolean;
}

export function startErase(board: BoardDoc, whole: boolean, at: Point, radius: number): EraseGesture {
  const g: EraseGesture = { whole, last: at, lines: new Map(), fills: new Map(), preview: {} };
  eraseTo(board, g, at, radius);
  return g;
}

/**
 * Erases along the segment from the last position to `to`. `radius` is in world units.
 * Returns whether the preview changed.
 */
export function eraseTo(board: BoardDoc, g: EraseGesture, to: Point, radius: number): boolean {
  const from = g.last;
  g.last = to;
  // circles close enough together that the band between them is covered
  const steps = Math.min(512, Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / Math.max(radius / 2, 1e-6))));
  const centres: Point[] = [];
  for (let i = 0; i <= steps; i++) centres.push({ x: from.x + ((to.x - from.x) * i) / steps, y: from.y + ((to.y - from.y) * i) / steps });
  const area = {
    x: Math.min(from.x, to.x) - radius,
    y: Math.min(from.y, to.y) - radius,
    w: Math.abs(to.x - from.x) + radius * 2,
    h: Math.abs(to.y - from.y) + radius * 2,
  };
  // pieces never leave their stroke's box, so the document's boxes still find them
  const hits = board.index.query(area).filter((i): i is StrokeItem => isInk(i) && !board.isHidden(i) && !board.isLocked(i));

  if (g.whole) {
    const gone = hits.filter((item) => centres.some((c) => strokeHit(item, c, radius))).map((i) => i.id);
    if (gone.length) board.deleteItems(gone);
    return false;
  }

  let changed = false;
  const rubbed = new Set<string>();
  for (const item of hits) {
    if (isFill(item)) {
      if (rubFill(g, item, from, to, centres, radius)) {
        rubbed.add(item.id);
        changed = true;
      }
    } else if (cutLine(g, item, centres, radius)) {
      changed = true;
    }
  }
  // a later touch starts a new path: joining it to this one would rub out the fill in between
  for (const [id, w] of g.fills) if (!rubbed.has(id)) w.open = null;
  return changed;
}

/** Writes what the drag erased to the document, in one transaction. */
export function endErase(board: BoardDoc, g: EraseGesture): void {
  if (g.whole || (!g.lines.size && !g.fills.size)) return;
  board.transact(() => {
    for (const w of g.lines.values()) commitLine(board, w);
    for (const w of g.fills.values()) commitFill(board, w);
  });
}

const round = (v: number) => Math.round(v * 100) / 100;

// ---- fills ------------------------------------------------------------------

/** Adds the eraser's move to the fill's mask. Returns whether the eraser touched it. */
function rubFill(g: EraseGesture, found: StrokeItem, from: Point, to: Point, centres: Point[], radius: number): boolean {
  // the fill as the drag first found it: the work so far is in its frame
  const item = g.fills.get(found.id)?.item ?? found;
  const ring = strokeWorldPoints(item);
  if (!centres.some((c) => fillTouches(item, c, radius, ring))) return false;
  const { sx, sy } = strokeScale(item);
  const r = round(radius / Math.sqrt(sx * sy));
  const local = (p: Point): [number, number] => {
    const b = worldToBox(item, p);
    return [b.x / sx, b.y / sy];
  };
  let w = g.fills.get(item.id);
  if (!w) {
    w = { item, erased: [...(item.props.erased ?? [])], open: null };
    g.fills.set(item.id, w);
  }
  const path = w.open === null ? undefined : w.erased[w.open];
  if (path && path[0] === r) {
    path.push(...local(to));
  } else {
    w.erased.push(from.x === to.x && from.y === to.y ? [r, ...local(to)] : [r, ...local(from), ...local(to)]);
    w.open = w.erased.length - 1;
  }
  g.preview = { ...g.preview, [item.id]: { erased: [...w.erased] } };
  return true;
}

function commitFill(board: BoardDoc, w: FillWork): void {
  const item = board.index.get(w.item.id);
  if (!item || !isInk(item)) return;
  // paths from before this drag stay as they are; the new ones are thinned out
  const kept = w.item.props.erased?.length ?? 0;
  const erased = w.erased.map((path, i) => (i < kept ? path : simplifyPath(path)));
  if (fillGone({ ...item, props: { ...item.props, erased } })) board.deleteItems([item.id]);
  else board.updateItem(item.id, { props: { erased } });
}

/**
 * Drops the points of an eraser path [radius, x, y, ...] that hardly bend it (Ramer-Douglas-
 * Peucker, within a tenth of the radius) and rounds the rest.
 */
function simplifyPath(path: number[]): number[] {
  const r = path[0]!;
  const n = (path.length - 1) / 2;
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const eps2 = (r / 10) ** 2;
  const px = (i: number) => path[1 + i * 2]!;
  const py = (i: number) => path[2 + i * 2]!;
  const stack: [number, number][] = n > 2 ? [[0, n - 1]] : [];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    let worst = -1;
    let worstD = eps2;
    for (let i = a + 1; i < b; i++) {
      const d = segDist2(px(i), py(i), px(a), py(a), px(b), py(b));
      if (d > worstD) {
        worst = i;
        worstD = d;
      }
    }
    if (worst < 0) continue;
    keep[worst] = 1;
    stack.push([a, worst], [worst, b]);
  }
  const out = [r];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(round(px(i)), round(py(i)));
  return out;
}

function segDist2(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return (ax + t * dx - px) ** 2 + (ay + t * dy - py) ** 2;
}

const PROBE_PX = 128;
let probe: HTMLCanvasElement | null = null;

/** Has the eraser rubbed out all of the fill? Renders it small and looks for any paint left. */
function fillGone(item: StrokeItem): boolean {
  const k = PROBE_PX / Math.max(item.w, item.h, 1e-6);
  probe ??= document.createElement('canvas');
  probe.width = Math.max(1, Math.ceil(item.w * k));
  probe.height = Math.max(1, Math.ceil(item.h * k));
  const ctx = probe.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  ctx.setTransform(k, 0, 0, k, 0, 0);
  paintStroke(ctx, { ...item, props: { ...item.props, opacity: 1 } });
  const data = ctx.getImageData(0, 0, probe.width, probe.height).data;
  for (let i = 3; i < data.length; i += 4) if (data[i]! > 12) return false;
  return true;
}

// ---- lines ------------------------------------------------------------------

/** Cuts what the circles cover out of the line's pieces so far. Returns whether anything went. */
function cutLine(g: EraseGesture, found: StrokeItem, centres: Point[], radius: number): boolean {
  const w = g.lines.get(found.id);
  // the line as the drag first found it: the pieces so far are in its frame
  const item = w?.item ?? found;
  const runs0 = w ? w.runs : [{ p: boxPoints(item), head: true, tail: true }];
  // the ink reaches half the line's width past the centre-line: cut that much further, so the
  // round ends of the pieces stop at the eraser's edge
  const reach = radius + strokeHalfWidth(item);
  let runs = runs0;
  let changed = false;
  for (const c of centres) {
    const b = worldToBox(item, c);
    const next: Run[] = [];
    for (const run of runs) {
      const cut = clipRun(run, b.x, b.y, reach);
      if (cut) changed = true;
      next.push(...(cut ?? [run]));
    }
    runs = next;
  }
  if (!changed) return false;
  runs = runs.filter((r) => runLength(r.p) > 1e-3);
  if (w) w.runs = runs;
  else g.lines.set(item.id, { item, runs });
  const { sx, sy } = strokeScale(item);
  const oldCut = item.props.cut ?? 0;
  g.preview = {
    ...g.preview,
    [item.id]: {
      runs: runs.map((run) => {
        const points: number[] = [];
        for (let i = 0; i + 2 < run.p.length; i += 3) points.push(run.p[i]! / sx, run.p[i + 1]! / sy, run.p[i + 2]!);
        return { points, cut: cutFlags(run, oldCut) };
      }),
    },
  };
  return true;
}

/** The stroke's points in its box frame. */
function boxPoints(item: StrokeItem): number[] {
  const { sx, sy } = strokeScale(item);
  const pts = item.props.points;
  const flat: number[] = [];
  for (let i = 0; i + 2 < pts.length; i += 3) flat.push(pts[i]! * sx, pts[i + 1]! * sy, pts[i + 2]!);
  return flat;
}

/** A piece keeps the original's flag for an end it shares with it; ends made by a cut are flagged. */
const cutFlags = (run: Run, oldCut: number): number => (run.head ? oldCut & 1 : 1) | (run.tail ? oldCut & 2 : 2);

/** Replaces the stroke with its pieces: the first keeps the id, the rest go right above it. */
function commitLine(board: BoardDoc, w: LineWork): void {
  const item = board.index.get(w.item.id);
  if (!item || !isInk(item)) return;
  if (!w.runs.length) {
    board.deleteItems([item.id]);
    return;
  }
  const oldCut = w.item.props.cut ?? 0;
  const pieces = w.runs.map((run) => {
    const piece = pieceOf(w.item, run.p);
    return { ...piece, props: { ...piece.props, cut: cutFlags(run, oldCut) } };
  });
  const [first, ...rest] = pieces;
  board.updateItem(item.id, first!);
  if (!rest.length) return;
  const all = board.index.all();
  const at = all.indexOf(item);
  const above = at < 0 ? undefined : all[at + 1];
  const ceiling = above && above.layerId === item.layerId ? above.z : null;
  let z = item.z;
  const inputs: NewItem[] = rest.map((piece) => {
    z = safeKeyBetween(z, ceiling);
    return {
      type: 'stroke',
      x: piece.x,
      y: piece.y,
      w: piece.w,
      h: piece.h,
      rotation: item.rotation,
      z,
      groupId: item.groupId,
      props: { ...item.props, ...piece.props },
    };
  });
  board.createItems(inputs);
}

/**
 * Removes the part of a run inside the circle. Returns null when the circle misses it, else the
 * pieces left (possibly none).
 */
function clipRun(run: Run, cx: number, cy: number, r: number): Run[] | null {
  const p = run.p;
  const n = p.length / 3;
  const r2 = r * r;
  const inside = (i: number) => (p[i * 3]! - cx) ** 2 + (p[i * 3 + 1]! - cy) ** 2 < r2;
  if (n === 1) return inside(0) ? [] : null;
  const out: Run[] = [];
  let touched = false;
  let cur: number[] = [];
  let curHead = false;
  if (inside(0)) touched = true;
  else {
    cur.push(p[0]!, p[1]!, p[2]!);
    curHead = run.head;
  }
  for (let i = 0; i + 1 < n; i++) {
    const ax = p[i * 3]!;
    const ay = p[i * 3 + 1]!;
    const ap = p[i * 3 + 2]!;
    const bx = p[i * 3 + 3]!;
    const by = p[i * 3 + 4]!;
    const bp = p[i * 3 + 5]!;
    const dx = bx - ax;
    const dy = by - ay;
    const fx = ax - cx;
    const fy = ay - cy;
    const a = dx * dx + dy * dy;
    const c = fx * fx + fy * fy - r2;
    // the part of the segment inside the circle, as parameters [s, e] along it
    let s = 1;
    let e = 0;
    if (a < 1e-12) {
      if (c < 0) {
        s = 0;
        e = 1;
      }
    } else {
      const bq = 2 * (fx * dx + fy * dy);
      const disc = bq * bq - 4 * a * c;
      if (disc > 0) {
        const q = Math.sqrt(disc);
        s = Math.max(0, (-bq - q) / (2 * a));
        e = Math.min(1, (-bq + q) / (2 * a));
      }
    }
    if (s >= e) {
      if (!cur.length) cur.push(ax, ay, ap);
      cur.push(bx, by, bp);
      continue;
    }
    touched = true;
    if (s > 0) {
      if (!cur.length) cur.push(ax, ay, ap);
      cur.push(ax + dx * s, ay + dy * s, ap + (bp - ap) * s);
    }
    if (cur.length >= 6) out.push({ p: cur, head: curHead, tail: false });
    cur = [];
    curHead = false;
    if (e < 1) cur.push(ax + dx * e, ay + dy * e, ap + (bp - ap) * e, bx, by, bp);
  }
  if (!touched) return null;
  if (cur.length >= 6) out.push({ p: cur, head: curHead, tail: run.tail });
  return out;
}

function runLength(p: number[]): number {
  let len = 0;
  for (let i = 3; i + 2 < p.length; i += 3) len += Math.hypot(p[i]! - p[i - 3]!, p[i + 1]! - p[i - 2]!);
  return len;
}

/**
 * The geometry of a stroke holding just this run: a box around it (padded as when drawn) with
 * the original's rotation and scale, placed so every point stays where it was.
 */
function pieceOf(item: StrokeItem, run: number[]) {
  const { sx, sy } = strokeScale(item);
  let u0 = Infinity;
  let v0 = Infinity;
  let u1 = -Infinity;
  let v1 = -Infinity;
  for (let i = 0; i + 2 < run.length; i += 3) {
    u0 = Math.min(u0, run[i]!);
    u1 = Math.max(u1, run[i]!);
    v0 = Math.min(v0, run[i + 1]!);
    v1 = Math.max(v1, run[i + 1]!);
  }
  const padU = item.props.size * sx;
  const padV = item.props.size * sy;
  u0 -= padU;
  u1 += padU;
  v0 -= padV;
  v1 += padV;
  // renderers divide by max(1, w0): keep the drawn size at least 1 so the scale is exact
  if (u1 - u0 < sx) {
    const grow = (sx - (u1 - u0)) / 2;
    u0 -= grow;
    u1 += grow;
  }
  if (v1 - v0 < sy) {
    const grow = (sy - (v1 - v0)) / 2;
    v0 -= grow;
    v1 += grow;
  }
  const w = u1 - u0;
  const h = v1 - v0;
  // move the box centre by the same offset, turned with the item
  const du = u0 + w / 2 - item.w / 2;
  const dv = v0 + h / 2 - item.h / 2;
  const rad = (item.rotation || 0) * DEG;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const cx = item.x + item.w / 2 + du * cos - dv * sin;
  const cy = item.y + item.h / 2 + du * sin + dv * cos;
  const points: number[] = [];
  for (let i = 0; i + 2 < run.length; i += 3) points.push(round((run[i]! - u0) / sx), round((run[i + 1]! - v0) / sy), round(run[i + 2]!));
  return {
    x: round(cx - w / 2),
    y: round(cy - h / 2),
    w: round(w),
    h: round(h),
    props: { points, w0: round(w / sx), h0: round(h / sy) },
  };
}
